import { mapGetters } from 'vuex'
import { cloneDeep } from 'lodash-es'
import { download, getDatedFilename, getErrorMessage, HAZARD_URL, RESOLVER_URL, isValidChemical } from '@/main'
import { hazardReportFilteredOrdered } from '@/store/hazard'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import { hazardAppearancePreference } from '@/components/hazard/hazardPreferences'
import { showErrorToastWithTimeout } from '@/utils/toast'

const defaultHazardFilters = () => ({
  profile: null,
  sourcesFilter: [],
  testsFilter: [],
  customProfile: [],
  hideND: true,
  chemicalStr: null,
})

const getPersistedHazardFilters = (filters) => {
  const { hideND: _hideND, ...persistedFilters } = filters || {}
  return persistedFilters
}

export default {
  data() {
    return {
      downloading: false,
      reportData: null,
      error: null,
      filters: defaultHazardFilters(),
      showUnlikely: false,
      selectedStructures: [],
      allowSingleMode: false,
      singleMode: false,
      defaultVTooltipProps,
    }
  },

  computed: {
    ...mapGetters([
      'getCheckedStructures',
      'getStructures',
      'getHazardReport',
      'getHazardProfiles',
      'getHazardReportFilters',
      'getHazardCategories',
      'getHazardEndpoints',
      'getHazardCategoriesHierarchy',
      'getHazardSourceTypes',
      'getHazardTestTypes',
      'getHazardSortBy',
      'getAddedFilters',
      'feature',
    ]),
    showImages: {
      get() {
        return hazardAppearancePreference.value.showImages
      },
      set(value) {
        hazardAppearancePreference.value = {
          ...hazardAppearancePreference.value,
          showImages: Boolean(value),
        }
      },
    },
    HAZARD_URL() {
      return HAZARD_URL
    },
    reportId() {
      return this.$route.params.id
    },
    routeChemId() {
      if (this.$route.params.chemId) {
        return this.$route.params.chemId
      }
      if (this.$route.query.ids) {
        return this.$route.query.ids
      }
      if (this.$route.query.sid) {
        return this.$route.query.sid
      }

      return null
    },
    report() {
      const report = this.reportId || this.routeChemId ? this.reportData : this.getHazardReport
      return report && !Array.isArray(report) ? report : null
    },
    chemId() {
      if (this.routeChemId) {
        return this.routeChemId
      }
      if (this.report) {
        return this.report.hazardChemicals[0].chemical.chemId
      }

      return null
    },
    effectiveFilters() {
      return {
        ...defaultHazardFilters(),
        ...(this.filters || {}),
      }
    },
    sourcesFiltered() {
      return (
        this.effectiveFilters.sourcesFilter.length !==
        this.getHazardSourceTypes.reduce((count, sourceType) => count + sourceType.children.length, 0)
      )
    },
    testTypesFiltered() {
      return (
        this.effectiveFilters.testsFilter.length !==
        this.getHazardTestTypes.reduce((count, testType) => count + testType.children.length, 0)
      )
    },
    profiles() {
      return [
        { value: null, text: 'Full' },
        { value: 'custom', text: 'Custom' },
        ...this.getHazardProfiles.map((profile) => ({ value: profile.id, text: profile.name })),
      ]
    },
    filteredReport() {
      const report = cloneDeep(this.report)
      if (!report) {
        return null
      }

      if (this.sourcesFiltered || this.testTypesFiltered) {
        const scores = {
          'VH': 6,
          'H': 5,
          'M': 4,
          'L': 3,
          'I': 2,
          'N/A': 1,
          'ND': 0,
        }
        const authorities = {
          'Authoritative': 2,
          'Screening': 1,
          'QSAR Model': 0,
        }

        report.hazardChemicals.forEach((chemical) =>
          chemical.scores.map((score) => {
            score.records = score.records.filter(
              (record) =>
                (this.effectiveFilters.sourcesFilter.includes(record.source) ||
                  this.effectiveFilters.sourcesFilter.includes(record.sourceOriginal)) &&
                ((this.effectiveFilters.testsFilter.includes('Not Available') && !record.testType) ||
                  this.effectiveFilters.testsFilter.includes(record.testType)),
            )

            if (!score.records.length) {
              delete score.finalAuthority
              delete score.finalScoreSource
              score.finalScore = 'ND'
              score.records = []
              return score
            }

            let finalScore, finalAuthority, finalScoreSource
            score.records.forEach((record) => {
              if (finalAuthority == null || authorities[record.listType] > authorities[finalAuthority]) {
                finalAuthority = record.listType
                finalScoreSource = record.source
                if (finalScore == null || scores[record.score] > scores[finalScore]) finalScore = record.score
              }
            })
            score.finalScore = finalScore
            score.finalAuthority = finalAuthority
            score.finalScoreSource = finalScoreSource
            return score
          }),
        )
      }

      if (this.effectiveFilters.chemicalStr) {
        const filterChemicalStr = this.effectiveFilters.chemicalStr.toLowerCase()
        report.hazardChemicals = report.hazardChemicals.filter((value) =>
          this.filterChemical(value.chemical, filterChemicalStr),
        )
      }

      if (this.effectiveFilters.hideND) {
        report.hazardChemicals = report.hazardChemicals.filter((chemical) =>
          chemical.scores.find((score) => score.finalScore !== 'ND'),
        )
      }

      report.hazardChemicals.forEach((chemical) => {
        const position = this.selectedStructures.findIndex((item) => chemical.chemical.chemId === item.chemId)
        chemical.chemical.checked = position !== -1
      })
      return report
    },
    recordsForTransfer() {
      return cloneDeep(this.selectedStructures)
    },
    selectedStructuresAsSet() {
      return new Set(this.selectedStructures.map((structure) => structure.smiles))
    },
    getStructuresAsMap() {
      return new Map(this.getStructures.map((structure) => [structure.chemical?.inchiKey, structure]))
    },
    hazardExportItems() {
      return [
        {
          title: 'Export to SDF',
          icon: 'mdi-file-download-outline',
          onClick: () => this.onHazardExport('SDF'),
          description: 'Export the hazard report to SDF format',
        },
        {
          title: 'Export to XLSX',
          icon: 'mdi-file-excel-outline',
          onClick: () => this.onHazardExport('XLSX'),
          description: 'Export the hazard report to XLSX format',
        },
        {
          title: 'Export to XLSX (split)',
          icon: 'mdi-file-table-outline',
          onClick: () => this.onHazardExport2('XLSX'),
          description: 'Export to XLSX, hazard records are split into different sheets',
        },
      ]
    },
  },

  watch: {
    'filters': {
      handler(value) {
        hazardAppearancePreference.value = {
          ...hazardAppearancePreference.value,
          hideND: value?.hideND ?? true,
        }
        this.$store.commit('SET_HAZARD_REPORT_FILTERS', getPersistedHazardFilters(value))
        this.syncSelectionWithVisibleHazardReport()
      },
      deep: true,
    },
    'report': {
      handler(report) {
        this.allowSingleMode = Boolean(report?.hazardChemicals?.length === 1)

        if (!this.allowSingleMode) {
          this.singleMode = false
        }

        if (report?.hazardChemicals?.length) {
          this.onSelectAllStructures()
        } else {
          this.selectedStructures = []
        }
      },
      immediate: true,
    },
    '$route.params': {
      handler() {
        location.reload()
      },
      immediate: false,
    },
  },

  mounted() {
    this.onSelectAllStructures()
  },

  methods: {
    updateShowImages(value) {
      this.showImages = value
    },
    updateShowUnlikely(value) {
      this.showUnlikely = value
    },
    onAclUpdated(acl) {
      this.report.acl = acl
    },
    onReset() {
      if (this.reportId) {
        this.$axios.delete(`${HAZARD_URL}/reports/${this.report.id}`).then(() => {
          if (this.report.id === this.getHazardReport.id) this.$store.commit('RESET_HAZARD_REPORT')
        })
        this.$router.push('/hazard/reports')
      } else {
        this.$store.commit('RESET_HAZARD_REPORT')
        this.$router.push('/hazard')
      }
    },
    onHazardExport(format) {
      this.downloading = true

      const hazardChemicals = hazardReportFilteredOrdered(
        this.report,
        this.getAddedFilters,
        this.getHazardSortBy,
        this.showUnlikely,
      ).filter((chemical) => this.selectedStructuresAsSet.has(chemical.chemical.smiles))

      hazardChemicals.forEach((hazardChemical) => {
        hazardChemical.requestChemical.chemical.additionalProps = this.getStructuresAsMap.get(
          hazardChemical.requestChemical.chemical.inchiKey,
        )?.chemical?.additionalProps
      })

      const report = {
        hazardChemicals,
      }

      this.$axios
        .post(
          `${HAZARD_URL}/report/export`,
          {
            report,
            outputFormat: format,
            showImages: this.showImages,
          },
          { responseType: 'blob' },
        )
        .then((response) => {
          const filename = getDatedFilename('hazard', format.toLowerCase())
          download(filename, response.data, format.toLowerCase())
        })
        .catch((error) => {
          this.error = getErrorMessage(error)
          showErrorToastWithTimeout(error, 'Error during export')
        })
        .finally(() => {
          this.downloading = false
        })
    },

    onHazardExport2(format) {
      this.downloading = true

      const report = {
        hazardChemicals: hazardReportFilteredOrdered(
          this.report,
          this.getAddedFilters,
          this.getHazardSortBy,
          this.showUnlikely,
        ),
      }

      this.$axios
        .post(
          `${HAZARD_URL}/report/export2`,
          {
            report,
            lockFirstChemical: ['SIMILAR', 'SUBSTRUCTURE'].includes(this.report.options.analogsSearchType),
            outputFormat: format,
            showImages: this.showImages,
          },
          { responseType: 'blob' },
        )
        .then((response) => {
          const filename = getDatedFilename('hazard2', format.toLowerCase())
          download(filename, response.data, format.toLowerCase())
        })
        .catch((error) => {
          this.error = getErrorMessage(error)
          showErrorToastWithTimeout(error, 'Error during export')
        })
        .finally(() => {
          this.downloading = false
        })
    },
    onReportSaved(report) {
      this.reportData = report
      this.$router.push(`/hazard/reports/${encodeURIComponent(report.id)}`)
    },
    onReportUpdated(report) {
      this.reportData = report
    },
    getData() {
      if (this.reportId) {
        this.$axios.get(`${HAZARD_URL}/reports/${this.reportId}`).then((response) => {
          response.data.hazardChemicals.forEach((chemical) => {
            chemical.chemical.checked = false
          })
          this.reportData = response.data
        })
      }
    },
    buildReportByIds() {
      const searchIds = this.chemId.split(',')
      if (searchIds.length === 1) {
        this.allowSingleMode = true
        this.singleMode = true
      }
      this.$axios
        .post(`${RESOLVER_URL}/lookup`, { ids: searchIds })
        .then((response) => {
          response.data.forEach((item) => {
            if (isValidChemical(item)) item.checked = true
          })
          this.found = response.data
          const chemicals = response.data.filter((item) => item.chemical).map((item) => item.chemical)
          if (chemicals.length > 0) {
            this.$store.dispatch('getHazardReport', {
              chemicals,
              options: this.$store.getters.getHazardOptions,
            })
          } else {
            this.reportData = null
            this.error = getErrorMessage(response.data[0])
          }
        })
        .catch((error) => {
          this.error = getErrorMessage(error)
        })
    },
    onStructureCheckboxClick(structure) {
      const position = this.selectedStructures.findIndex((item) => structure.chemId === item.chemId)

      if (position !== -1) this.selectedStructures.splice(position, 1)
      else this.selectedStructures.push(structure)
    },
    onSelectAllStructures() {
      this.selectedStructures = []
      hazardReportFilteredOrdered(
        this.filteredReport,
        this.getAddedFilters,
        this.getHazardSortBy,
        this.showUnlikely,
      ).forEach((chemical) => {
        this.selectedStructures.push(chemical.chemical)
      })
    },
    onClearSelection() {
      this.selectedStructures = []
    },
    getVisibleHazardChemicals() {
      if (!this.filteredReport) return []

      return hazardReportFilteredOrdered(
        this.filteredReport,
        this.getAddedFilters,
        this.getHazardSortBy,
        this.showUnlikely,
      )
    },
    syncSelectionWithVisibleHazardReport() {
      const visibleChemIds = new Set(this.getVisibleHazardChemicals().map((item) => item.chemical.chemId))

      this.selectedStructures = this.selectedStructures.filter((chemical) => visibleChemIds.has(chemical.chemId))
    },
    onFilterSelection() {
      this.syncSelectionWithVisibleHazardReport()
    },
    filterChemical(value, search) {
      return (
        value &&
        ((value.sid && value.sid.toLowerCase().includes(search)) ||
          (value.name && value.name.toLowerCase().includes(search)) ||
          (value.casrn && value.casrn.toLowerCase().includes(search)))
      )
    },
  },

  created() {
    this.filters = {
      ...defaultHazardFilters(),
      ...(this.getHazardReportFilters || {}),
      hideND: hazardAppearancePreference.value.hideND,
    }
    if (this.reportId) {
      this.getData()
    } else if (this.routeChemId) {
      this.buildReportByIds()
    }
  },
}
